import { useLocalization } from '@magicdoorinc/ui';
import { emptyPlaceholder } from '~/utils/constant';
import type { StatusConfig } from '@magicdoorinc/ui';

const { t } = useLocalization();

type DictItem<T, Meta> = { label: string; value: T; meta?: Meta };

export class Dict<T, Meta = Record<string, any>> {
  constructor(
    protected readonly dict: Array<DictItem<T, Meta>>,
    public readonly options: { defaultMeta?: Meta; labelPlaceholder?: string } = {}
  ) {}

  getLabelByValue(value: T | undefined, raw: boolean = false): string {
    const option = this.dict.find((opt) => opt.value === value);
    const { t } = useLocalization();
    if (option) {
      return raw ? option.label : t(option.label);
    }
    return this.options.labelPlaceholder || emptyPlaceholder;
  }

  getMetaByValue(value: T | undefined): Meta | undefined {
    const option = this.dict.find((opt) => opt.value === value);
    return option?.meta || this.options.defaultMeta;
  }

  getOptions(raw: boolean = false): Array<{ label: string; value: T; meta?: Meta }> {
    return this.dict.map((opt) => ({ label: raw ? opt.label : t(opt.label), value: opt.value, meta: opt.meta }));
  }

  getStatusMap(): Record<string, StatusConfig> {
    return this.dict.reduce(
      (acc, opt) => {
        const meta = opt.meta as { color: string };
        if (!meta.color) {
          throw new Error(`Color is required for status ${opt.label}`);
        }
        acc[opt.value as string] = { label: t(opt.label), color: meta.color };
        return acc;
      },
      {} as Record<string, StatusConfig>
    );
  }

  cloneAndExtend(ext: (item: DictItem<T, Meta>) => DictItem<T, Meta>): Dict<T, Meta> {
    return new Dict(this.dict.map((item) => ext(item)));
  }
}
